<template>
  <div class="model" v-loading="loading" element-loading-text="正在加载模型">
    <model-layout
        class="model-content"
        :autoSize="autoSize"
        :model-info="modelInfo"
        :is-speech="onSpeech"
    >
      <template #top>
        <button class="model-button" v-show="showBtn" @click="onBack">
          <i class="el-icon-back"></i>返回
        </button>
      </template>
      <div slot="bottom">
        <Steps
            :space="200"
            :active="activeStep > 5 ? 5 : activeStep"
            process-status="success"
            align-center
            v-if="isPlay"
        >
          <Step
              v-for="item in stepList.slice(0, 4)"
              :key="item.id"
              :title="item.name"
          ></Step>
        </Steps>
        <Steps
            :active="activeStep - 4"
            process-status="success"
            align-center
            v-if="isPlay && stepList.length >= 4"
            :space="260"
        >
          <Step
              v-for="(item, index) in stepList.slice(4)"
              :key="item.id"
              :title="item.name"
          >
            <div slot="icon" style="font-weight: 700">{{ index + 5 }}</div>
          </Step>
        </Steps>
      </div>
    </model-layout>
    <div id="model"></div>
  </div>
</template>

<script setup>

import vue, {
  computed,
  defineProps,
  h,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from "vue";
import * as THREE from "three";
import { CSS2DObject, CSS3DObject } from "three/addons";
import ModelItems from "@/components/model/model.json";
import ModelMarker from "@/views/factory/modules/ModelMarker.vue";
import * as TWEEN from "three/examples/jsm/libs/tween.module";
import { useThree } from "@/components/model/useThree";
import Roam from "@/components/model/roam";
import ModelLayout from "@/components/model/ModelLayout.vue";
import { Step, Steps } from "element-ui";
import store from "@/store";
import PumpMarker from "@/components/model/modules/pump-marker.vue";
import PipePopup from "@/components/model/modules/pipe-popup.vue";

const props = defineProps({
  autoSize: { type: Boolean, default: false },
  position: { type: Array, default: null },
});

const {
  renderer,
  loader,
  scene,
  camera,
  cssRenderer,
  css3DRenderer,
  controls,
  raycaster,
  mouse,
  water,
} = useThree(props);
const {
  start,
  stop,
  pause,
  isPlay,
  isPause,
  modelInfo,
  activeStep,
  onSpeech,
  skip,
} = Roam(scene, camera, controls);

const materialName = [
  "_13",
  "pasted___13",
  "shui",
  "sm6",
  "J14:pasted__shui8",
  "_13.001",
  "pasted___13.001",
  "J14:pasted__shui8.001",
];
const emits = defineEmits(["init", "showDetail", "tagRendered"]);
const showBtn = ref(false);
const current = ref(null);
const rendered = ref(false);
const animationId = ref(null);
const opacity = ref(1);
const show = ref(false);
const showPlayer = ref(false);
const loading = ref(true);
const keyboard = {};
const speed = ref(1);
const stepList = ref([{ id: "J12", name: "办公室" }]);
let highlightedFace = null; //高亮
let highlightedMesh =  null; //高亮de mesh
let cameraPosition = null;
let controlTarget = null;
const pipeInfoBox = document.createElement("div");
const labelObject = new CSS2DObject(pipeInfoBox);
labelObject.name = "pipeInfo";
labelObject.visible = false;
labelObject.position.set(0, 0, 0);

const onKeydown = (event) => {
  keyboard[event.key] = true;
};

const onKeyup = (event) => {
  keyboard[event.key] = false;
};

const startControl = () => {
  document.addEventListener("keydown", onKeydown);
  document.addEventListener("keyup", onKeyup);
};

const stopControl = () => {
  document.removeEventListener("keydown", onKeydown);
  document.removeEventListener("keyup", onKeyup);
};

watch(isPlay, (val) => {
  if (val && isPause) {
    startControl();
  } else {
    stopControl();
    stepList.value = [{ id: "J12", name: "办公室" }];
  }
});

watch(
    modelInfo,
    (val) => {
      if (!!val && isPlay.value) {
        stepList.value.push(val);
      } else if (!val && !isPlay.value) {
        stepList.value = [{ id: "J12", name: "办公室" }];
      }
    },
    { immediate: true }
);
watch(
    () => store.state.showPipe,
    (val) => {
      if (val) {
        document.addEventListener("click", onPipeClick);
      } else {
        document.removeEventListener("click", onPipeClick);
        const pipeInfo = scene.getObjectByName("pipeInfo");
        pipeInfo.visible = false;
      }
    }
);
const render = () => {
  const sceneDomElement = document.getElementById("model");
  sceneDomElement.appendChild(renderer.domElement);

  const animate = () => {
    animationId.value = requestAnimationFrame(animate);
    TWEEN.update();
    renderer.render(scene, camera);
    cssRenderer.render(scene, camera);
    css3DRenderer.render(scene, camera);

    water.material.uniforms["time"].value += 1.0 / 60.0;
    if (isPlay.value && isPause.value) {
      const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(
          camera.quaternion
      );
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(
          camera.quaternion
      );
      if (keyboard["Control"]) {
        if (camera.position.y > 1) {
          camera.position.y -= speed.value;
        }
      }
      if (keyboard[" "]) {
        camera.position.y += speed.value;
      }
      if (keyboard["w"]) {
        camera.position.add(forward.clone().multiplyScalar(speed.value));
      }
      if (keyboard["s"]) {
        camera.position.add(forward.clone().multiplyScalar(-speed.value));
      }
      if (keyboard["a"]) {
        camera.position.add(right.clone().multiplyScalar(-speed.value));
      }
      if (keyboard["d"]) {
        camera.position.add(right.clone().multiplyScalar(speed.value));
      }
    }
  };
  if (scene.getObjectByName("Scene")) {
    animate();
    onAddTags();
    camera.position.set(0, 200, 200);
    camera.lookAt(0, 0, 0);
    loading.value = false;
    return;
  }
  onAddDetail();
  loader.load(process.env.VUE_APP_BASE + "models/now_pipe2.glb", (gltf) => {
    // gltf.scene.add(labelObject);
    gltf.scene.name = 'now-pipe2'
    scene.add(gltf.scene);
  });
  // loader.load(process.env.VUE_APP_BASE + "models/now-pipe.glb", (gltf) => {
  //   // gltf.scene.add(labelObject);
  //   scene.add(gltf.scene);
  // });
  loader.load(process.env.VUE_APP_BASE + "models/drug.glb", (gltf) => {
    gltf.scene.name = 'drug'
    scene.add(gltf.scene);
  });
  loader.load(process.env.VUE_APP_BASE + "models/long-pipe.glb", (gltf) => {
    gltf.scene.add(labelObject);
    const model = gltf.scene;
    // // 将模型放在地面上
    // model.traverse(function(child) {
    //   if (child.isMesh) {
    //     // 方法1：直接更改材质颜色
    //     // child.material.color.set(0x60bff1);
    //   }
    // });
    // model.position.y = -3.6;
    model.traverse((child) => {
      if (child.isMesh || child.isGroup) {
        if (child.name.indexOf("工艺管道")>-1) {
          child.visible = false;
        }
      }
    });

    scene.add(model);
  });
  loader.load(
      process.env.VUE_APP_BASE + "models/long.glb",
      (gltf) => {
        let gx = gltf.scene.getObjectByName("GX");
        gx.removeFromParent();
        const tagGroup = new THREE.Group();
        const pumpTagGroup = new THREE.Group();
        tagGroup.name = "tags";
        pumpTagGroup.name = "pumps";
        gltf.scene.add(tagGroup);
        gltf.scene.add(pumpTagGroup);
        scene.add(gltf.scene);
        gltf.scene.traverse((i) => {
          if (i.isMesh && materialName.includes(i.material.name)) {
            i.material = water.material;
          }
        });
        rendered.value = true;
        onAddTags();
        animate();
        loading.value = false;
      },
      undefined,
      (error) => {
        // console.error(error);
      }
  );
};

const onAddDetail = () => {
  ModelItems.forEach((item) => {
    if (item.path) {
      loader.load(
          process.env.VUE_APP_BASE + item.path,
          (m) => {
            m.scene.name = item.id + "-detail";
            m.scene.rotateX(THREE.MathUtils.degToRad(item.rotate[0]));
            m.scene.rotateY(THREE.MathUtils.degToRad(item.rotate[1]));
            m.scene.rotateZ(THREE.MathUtils.degToRad(item.rotate[2]));
            m.scene.position.set(
                item.position[0],
                item.position[1],
                item.position[2]
            );
            m.scene.visible = false;
            scene.add(m.scene);
            m.scene.traverse((i) => {
              if (i.isMesh && materialName.includes(i.material.name)) {
                i.material = water.material;
              }
            });
            if (item.id === "J12") {
              let mesh = m.scene.getObjectByName("pCube12");
              const box = new THREE.Box3().setFromObject(mesh);
              const center = new THREE.Vector3();
              box.getCenter(center);
              let scale = mesh.scale.clone();
              const div = document.createElement("div");
              div.style.backgroundColor = "#000";
              const iframe = document.createElement("iframe");
              iframe.style.border = "0px";
              iframe.src = "/#/home?scale=true";
              div.appendChild(iframe);
              let object = new CSS3DObject(div);
              object.position.copy(center);
              object.scale.copy(scale);
              object.rotation.x = 0;
              object.rotation.y = -89.55;
              object.rotation.z = 0;
              m.scene.remove(mesh);
              m.scene.add(object);
              const sceneDomElement = document.getElementById("model");
              sceneDomElement.appendChild(css3DRenderer.domElement);
            }
          },
          undefined,
          (error) => {
            console.error(error);
          }
      );
    }
  });
};

const onAddTags = () => {
  const sceneDomElement = document.getElementById("model");
  const main = scene.getObjectByName("Main_scene");
  const tagGroup = scene.getObjectByName("tags");
  ModelItems.forEach((item) => {
    const obj = main.getObjectByName(item.id);
    const tagObj = main.getObjectByName(item.labelId ? item.labelId : item.id);
    const box = new THREE.Box3().setFromObject(tagObj);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const element = document.createElement("div");
    const marker = new vue({
      el: document.createElement("div"),
      render: () => {
        return h(ModelMarker, {
          attrs: { id: item.id },
          props: { item: item },
        });
      },
    }).$el;
    element.innerHTML = marker.outerHTML;
    element.style.pointerEvents = "auto";
    const tag = new CSS2DObject(element);
    if (item.path || item.children.length || item.clickable === true) {
      element.style.filter = "hue-rotate(250deg)";
    } else if (item.id === "J32") {
      element.style.filter = "grayscale(100%)";
    }
    element.addEventListener("contextmenu", (event) => {
      event.preventDefault();
    });
    const clickDom = element.getElementsByClassName("model-marker-title")[0];
    clickDom.addEventListener("click", () => {
      cameraPosition = camera.clone().position;
      controlTarget = controls.target;
      current.value = item.id;
      const tags = scene.getObjectByName("tags");
      const detail = scene.getObjectByName(item.id + "-detail");
      if (detail && obj.visible) {
        obj.visible = false;
        detail.visible = true;
        const detailBox = new THREE.Box3().setFromObject(detail);
        const detailCenter = new THREE.Vector3();
        detailBox.getCenter(detailCenter);
        const distance = Math.max(
            detailBox.max.x - detailBox.min.x,
            Math.max(
                detailBox.max.y - detailBox.min.y,
                detailBox.max.z - detailBox.min.z
            )
        );
        const direction = new THREE.Vector3();
        camera.getWorldDirection(direction);
        animateCameraFunc(
            camera.position,
            controls.target,
            item.focus.position
                ? item.focus.position
                : {
                  x: detailCenter.x,
                  y: detailCenter.y + 40,
                  z: detailCenter.z + 25,
                },
            item.focus.target ? item.focus.target : detailCenter
        );
      } else if (item.children.length || item.clickable === true) {
        item.children.forEach((child) => {
          const childDetail = scene.getObjectByName(child.id);
          if (childDetail) {
            childDetail.visible = false;
          }
        });
        animateCameraFunc(
            camera.position,
            controls.target,
            item.focus.position
                ? item.focus.position
                : {
                  x: center.x,
                  y: center.y + 40,
                  z: center.z + 25,
                },
            item.focus.target ? item.focus.target : center
        );
      } else {
        return;
      }
      if (item.pumps) {
        onAddPumpTags(item);
      }
      emits("showDetail", true);
      modelInfo.value = ModelItems.find((i) => i.id === item.id);
      store.commit("SetClear", true);
      store.commit("SetInside", true);
      showBtn.value = true;
      tags.visible = false;
    });
    if (obj) {
      tag.position.set(center.x, center.y, center.z);
      tagGroup.add(tag);
    }
  });
  sceneDomElement.appendChild(cssRenderer.domElement);
  emits("tagRendered");
};

const onAddPumpTags = (modelItem) => {
  const main = scene.getObjectByName(modelItem.id + "-detail");
  const pumps = scene.getObjectByName("pumps");

  modelItem.pumps.forEach((pump) => {
    const obj = main.getObjectByName(pump.positionId);
    const box = new THREE.Box3().setFromObject(obj);
    const center = new THREE.Vector3();
    box.getCenter(center);
    const element = document.createElement("div");
    const marker = new vue({
      el: document.createElement("div"),
      render: () => {
        return h(PumpMarker, {
          attrs: { id: pump.id },
          props: { data: pump },
        });
      },
    }).$el;
    element.innerHTML = marker.outerHTML;
    const tag = new CSS2DObject(element);
    tag.position.set(center.x, center.y, center.z);
    pumps.add(tag);
  });
};

const onDetailClick = (event) => {
  mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
  mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  raycaster.setFromCamera(mouse, camera);
  const detail = scene.getObjectByName(current.value + "-detail");
  const intersects = raycaster.intersectObjects(detail.children, true);
  if (intersects.length > 0) {
    const selectedObject = intersects[0].object.parent;
  }
};

const onBack = () => {
  const main = scene.getObjectByName("Main_scene");
  const tags = scene.getObjectByName("tags");
  const pumps = scene.getObjectByName("pumps");
  pumps.clear();
  tags.visible = true;
  emits("showDetail", false);
  const detail = scene.getObjectByName(current.value + "-detail");
  const obj = main.getObjectByName(current.value);
  const children = ModelItems.find((i) => i.id === current.value).children;
  if (detail) {
    detail.visible = false;
    obj.visible = true;
  } else if (children.length) {
    children.forEach((child) => {
      const childDetail = main.getObjectByName(child.id);
      if (childDetail) {
        childDetail.visible = true;
      }
    });
  }
  showBtn.value = false;
  store.commit("SetInside", false);
  const newPos = props.position
      ? new THREE.Vector3(props.position[0], props.position[1], props.position[2])
      : new THREE.Vector3(
          cameraPosition?.x ? cameraPosition.x : -59.70328374353009,
          cameraPosition?.y ? cameraPosition.y : 146.25649344845982,
          cameraPosition?.z ? cameraPosition.z : -124.37796988989662
      );
  const newTarget = new THREE.Vector3(
      controlTarget?.x ? controlTarget.x : -38.235557542198976,
      controlTarget?.y ? controlTarget.y : -10.091692931620278,
      controlTarget?.z ? controlTarget.z : -22.866813657955806
  );
  animateCameraFunc(camera.position, controls.target, newPos, newTarget);
  current.value = null;
  modelInfo.value = null;
};

const animateCameraFunc = (oldPos, oldTarget, newPos, newTarget) => {
  const tween = new TWEEN.Tween({
    x1: oldPos.x,
    y1: oldPos.y,
    z1: oldPos.z,
    x2: oldTarget.x,
    y2: oldTarget.y,
    z2: oldTarget.z,
  });
  tween.to(
      {
        x1: newPos.x,
        y1: newPos.y,
        z1: newPos.z,
        x2: newTarget.x,
        y2: newTarget.y,
        z2: newTarget.z,
      },
      1000
  );
  tween.easing(TWEEN.Easing.Cubic.InOut);
  tween.onUpdate((pos) => {
    camera.position.x = pos.x1;
    camera.position.y = pos.y1;
    camera.position.z = pos.z1;
    controls.target.x = pos.x2;
    controls.target.y = pos.y2;
    controls.target.z = pos.z2;
    controls.update();
  });
  tween.start();
};

const hideItem = (item) => {
  const object = scene.getObjectByName(item.id);
  if (object) {
    object.visible = item.show;
  }
};

const setOpacity = (opacityValue) => {
  const object = scene.getObjectByName("E");
  if (object) {
    object.children.map((item) => {
      item.traverse((mesh) => {
        if (mesh.isMesh) {
          mesh.material = mesh.material.clone();
          if (opacityValue !== 1) {
            mesh.material.transparent = true;
            mesh.material.opacity = opacityValue;
          } else {
            mesh.material.opacity = opacityValue;
            if (mesh.material.name !== "_68.001") {
              mesh.material.transparent = false;
            }
          }
        }
      });
    });
  }
};

const showInside = (show) => {
  const main = scene.getObjectByName("Main_scene");
  ModelItems.forEach((item) => {
    const detail = scene.getObjectByName(item.id + "-detail");
    const obj = main.getObjectByName(item.id);
    if (detail) {
      detail.visible = show;
      obj.visible = !show;
    } else if (item.children.length) {
      item.children.forEach((child) => {
        const childDetail = scene.getObjectByName(child.id);
        if (childDetail) {
          childDetail.visible = !show;
        }
      });
    }
  });
};

const startRoam = () => {
  start();
};

const stopRoam = () => {
  showPlayer.value = false;
  stop();
};

const pauseRoam = () => {
  pause();
};

const onNext = () => {
  if (isPause.value) {
    start();
  }
  skip.value = true;
};
const onResize = () => {
  // 更新相机的宽高比
  const width = window.innerWidth;
  const height = window.innerHeight;
  camera.aspect = width / height;
  camera.updateProjectionMatrix();

  // 更新渲染器的尺寸
  renderer.setSize(width, height);
  cssRenderer.setSize(width, height);
  css3DRenderer.setSize(width, height);
  // 如果启用了设备像素比，则调整渲染器的像素比
  renderer.setPixelRatio(window.devicePixelRatio);
};


const onPipeClick = (e) => {



  if (e.target.tagName !== "CANVAS") return;
  mouse.x = (e.clientX / window.innerWidth) * 2 - 1 + 0.001;
  mouse.y = -(e.clientY / window.innerHeight) * 2 + 1 - 0.01;
  // let pipe = scene.getObjectByName("GX");
  //
  const pipe = scene.getObjectByName("Scene");
  const pipe2 = scene.getObjectByName("now-pipe2"); //生产管线

  const pipeInfo = scene.getObjectByName("pipeInfo");
  raycaster.setFromCamera(mouse, camera);
  const intersects2 = raycaster.intersectObjects([pipe, pipe2], true);
  const intersection = intersects2[0];

  const pipeMesh = intersection.object||{};
  console.log(pipeMesh)
  if(pipeMesh.name?.indexOf('工艺管道')>-1||pipeMesh.name?.indexOf('GN')>-1 ){
    return false
  }

  if (intersects2.length > 0) {
    if(highlightedMesh){

      highlightedMesh.material = highlightedMesh.material.clone();
      highlightedMesh.material.color.set(highlightedFace); // 改为绿色
      highlightedMesh = null
    }


    highlightedMesh = scene.getObjectByName(pipeMesh.name);

    if (highlightedMesh) {
      highlightedFace = highlightedMesh.material.color
      // 如果需要创建新材质（保留其他属性）
      highlightedMesh.material = highlightedMesh.material.clone();
      highlightedMesh.material.color.set(0xfd7412); // 改为绿色
    }
    pipeInfo.position.set(intersection.point.x,intersection.point.y,intersection.point.z);
    pipeInfo.visible = true;

    const div = new vue({
      el: document.createElement("div"),
      render: () => {
        return h(PipePopup, {
          props: {
            name: pipeMesh.name,
            id: pipeMesh.id,
          },
          on: {
            close() {
              pipeInfo.visible = false;
              div.$destroy();
              pipeInfoBox.innerHTML = "";
              if(highlightedMesh){

                highlightedMesh.material = highlightedMesh.material.clone();
                highlightedMesh.material.color.set(highlightedFace); // 改为绿色
                highlightedMesh = null
              }
            },
          },
        });
      },
    });
    pipeInfoBox.innerHTML = div.$el.outerHTML;
    // }
  }
};
onMounted(() => {
  render();
  emits("init");
  window.addEventListener("resize", onResize);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onResize);
  window.removeEventListener("click", onPipeClick);
  cancelAnimationFrame(animationId.value);
  const tagGroup = scene.getObjectByName("tags");
  const pumpTagGroup = scene.getObjectByName("pumps");
  if (tagGroup) {
    tagGroup.clear();
  }
  if (pumpTagGroup) {
    pumpTagGroup.clear();
  }
  while (cssRenderer.domElement.firstChild) {
    cssRenderer.domElement.removeChild(cssRenderer.domElement.firstChild);
  }
  if (current.value) {
    onBack();
  }
  showPlayer.value = false;
});

defineExpose({
  showBtn,
  current,
  opacity,
  show,
  showPlayer,
  loading,
  render,
  onAddDetail,
  onAddTags,
  onDetailClick,
  onBack,
  hideItem,
  setOpacity,
  showInside,
  startRoam,
  stopRoam,
  pauseRoam,
  scene,
  onNext,
  materialName,
});
</script>

<style scoped lang="less">
.model {
  width: 100%;
  height: 100%;
  position: relative;

  ::v-deep(.el-loading-mask) {
    z-index: 50;

    .el-loading-spinner {
      .circular {
        width: 100px;
        height: 100px;
      }

      .el-loading-text {
        font-size: 20px;
      }
    }
  }

  #model {
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: inline-block;
  }

  .label {
    position: absolute;
  }

  &-button {
    position: absolute;
    top: 10px;
    right: 36px;
    width: 60px;
    height: 30px;
    font-size: 16px;
    background: url("@/components/images/button-bg.png") no-repeat;
    background-size: 100% 100%;
    color: #fff;
    border: unset;
    cursor: pointer;
  }

  &-player {
    position: absolute;
    bottom: 200px;
    left: 50%;
    right: 50%;
    transform: translateX(-50%);
    scale: 0.4;
    width: 500px;
    height: 100px;
    background: rgba(1, 15, 36, 0.5);
    border: 1px solid #85a5b5;
    display: flex;
    justify-content: space-evenly;
    align-items: center;

    div {
      width: 80px;
      height: 80px;
      cursor: pointer;
      border-radius: 50%;

      &:hover {
        filter: grayscale(30%);
        box-shadow: 0px 0px 10px rgba(255, 255, 255, 0.5);
      }
    }

    &-play {
      background: url("@/assets/images/pause.png");
      background-size: 100%;
    }

    &-pause {
      background: url("@/assets/images/play.png");
      background-size: 100%;
    }

    &-stop {
      background: url("@/assets/images/stop.png");
      background-size: 100%;
    }
  }

  &-content {
    &-left {
      p {
        font-size: 28px;
        color: #fff;
        margin-bottom: 10px;
      }
    }

    &-right {
      width: 100%;
      height: 100%;
      overflow-y: auto;
    }
  }

  ::v-deep(.el-steps) {
    justify-content: center;
    margin-top: 50px;

    .el-step__title {
      font-size: 14px;
      text-shadow: 0.0625rem 0.0625rem 0.0625rem #000000;
    }
  }
}
</style>
